import type { ReactNode } from "react";
import { Head } from "@/components/Head";
import { Button } from "@/components/Button";
import { CtaBand } from "@/components/CtaBand";
import { Section, SectionHeading, Eyebrow } from "@/components/Section";
import { Reveal } from "@/components/Reveal";
import { cn } from "@/lib/cn";

type FeatureVisual =
  | { kind: "icon"; icon: ReactNode }
  | { kind: "image"; src: string; alt: string };

type Feature = {
  tag: string;
  title: string;
  body: string;
  bullets: string[];
  visual: FeatureVisual;
};

function IconTile({ children }: { children: ReactNode }) {
  return (
    <div className="grid h-14 w-14 shrink-0 place-items-center rounded-2xl bg-teal/12 text-teal-deep">
      {children}
    </div>
  );
}

const icon = {
  board: (
    <svg
      width="26"
      height="26"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <rect
        x="3"
        y="4"
        width="18"
        height="17"
        rx="2.5"
        stroke="currentColor"
        strokeWidth="1.6"
      />
      <path d="M3 9h18" stroke="currentColor" strokeWidth="1.6" />
      <path
        d="M7 2.5v3M17 2.5v3"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
      <rect
        x="6"
        y="12"
        width="4.5"
        height="3.5"
        rx="0.8"
        fill="currentColor"
        opacity="0.5"
      />
      <rect
        x="13.5"
        y="12"
        width="4.5"
        height="3.5"
        rx="0.8"
        fill="currentColor"
        opacity="0.9"
      />
      <rect
        x="6"
        y="17"
        width="4.5"
        height="1.6"
        rx="0.6"
        fill="currentColor"
        opacity="0.35"
      />
    </svg>
  ),
  history: (
    <svg
      width="26"
      height="26"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="8.5" stroke="currentColor" strokeWidth="1.6" />
      <path
        d="M12 7.5V12l3 2"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ),
  invoice: (
    <svg
      width="26"
      height="26"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M6 3h9l4 4v14a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1Z"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinejoin="round"
      />
      <path
        d="M9 12h6M9 15.5h6M9 8.5h3"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
    </svg>
  ),
  shield: (
    <svg
      width="26"
      height="26"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M12 2.75 19.5 6v6.2c0 4.6-3.2 7.9-7.5 9.55-4.3-1.65-7.5-4.95-7.5-9.55V6L12 2.75Z"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinejoin="round"
      />
      <path
        d="M8.75 12.2 11 14.5l4.25-5"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ),
  calc: (
    <svg
      width="26"
      height="26"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <rect
        x="5"
        y="2.5"
        width="14"
        height="19"
        rx="2"
        stroke="currentColor"
        strokeWidth="1.6"
      />
      <path
        d="M7.75 6.5h8.5"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
      <circle cx="8.5" cy="11" r="1.1" fill="currentColor" />
      <circle cx="12" cy="11" r="1.1" fill="currentColor" />
      <circle cx="15.5" cy="11" r="1.1" fill="currentColor" />
      <circle cx="8.5" cy="14.75" r="1.1" fill="currentColor" />
      <circle cx="12" cy="14.75" r="1.1" fill="currentColor" />
      <circle cx="15.5" cy="14.75" r="1.1" fill="currentColor" />
      <circle cx="8.5" cy="18.5" r="1.1" fill="currentColor" />
      <circle cx="12" cy="18.5" r="1.1" fill="currentColor" />
      <circle cx="15.5" cy="18.5" r="1.1" fill="currentColor" />
    </svg>
  ),
  megaphone: (
    <svg
      width="26"
      height="26"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M3 10.5v3a1.2 1.2 0 0 0 1.2 1.2H6l1.4 4.6a1 1 0 0 0 1.9-.3l-.5-4.3 10.1 3.1a.7.7 0 0 0 .9-.67V6.87a.7.7 0 0 0-.9-.67L8.4 9.3H4.2A1.2 1.2 0 0 0 3 10.5Z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
    </svg>
  ),
};

const features: Feature[] = [
  {
    tag: "Dispatch & scheduling",
    title: "A board that reflects the real day, not the plan you made at 7am",
    body: "The dispatch board shows live capacity, tech skills, and zones in one decision surface, so a cancellation or a same-day emergency doesn't blow up the whole schedule. Jobs booked by AI voice arrive already structured — customer, property, and job type attached — ready to slot in without a CSR re-typing anything.",
    bullets: [
      "Live board for appointments, holds, and same-day emergencies",
      "Skills, zones, and workload weighed automatically for smarter assignment",
      "AI-booked jobs land pre-qualified, not as a raw voicemail to sort out",
    ],
    visual: {
      kind: "image",
      src: "/img/mktg-hero-dashboard.jpg",
      alt: "ServiTrex dispatch board showing today's job schedule, technician assignments, and live capacity across zones",
    },
  },
  {
    tag: "CRM & customer history",
    title:
      "Every customer, every property, every job — one record that never resets",
    body: "Equipment history, prior visits, notes, and open opportunities travel with the account, not with whichever CSR happened to take the last call. When a customer calls back eighteen months later about the same water heater, the person answering already knows the story.",
    bullets: [
      "Full service and equipment history attached to each property",
      "Calls, notes, and messages logged against the account automatically",
      "Sales opportunities and follow-ups tracked alongside service history",
    ],
    visual: { kind: "icon", icon: icon.history },
  },
  {
    tag: "Estimates & invoicing",
    title: "Quote, approve, and get paid without re-keying a single line",
    body: "Estimates pull from a pricebook that matches what the field actually charges, so quotes stop drifting from reality. Customers approve and pay deposits digitally before the truck rolls, and the invoice generates from the completed job — not a blank form someone has to rebuild from memory.",
    bullets: [
      "Pricebook-driven quoting that matches field pricing exactly",
      "Digital approvals and deposits collected before dispatch",
      "Invoices generated straight from the closed-out job",
    ],
    visual: { kind: "icon", icon: icon.invoice },
  },
  {
    tag: "Memberships & agreements",
    title: "Turn one-time jobs into recurring revenue with the Comfort Club",
    body: "Build tiered maintenance agreements by trade — priority scheduling, discounted rates, seasonal tune-ups — and let ServiTrex track renewals against real service history instead of a spreadsheet someone forgets to update. Comfort Club members become the most predictable revenue line in the business.",
    bullets: [
      "Tiered agreement plans built per trade and per brand",
      "Renewals tracked automatically against actual service visits",
      "Member perks and priority scheduling built into the dispatch flow",
    ],
    visual: { kind: "icon", icon: icon.shield },
  },
  {
    tag: "Call center & AI voice",
    title:
      "Answer 100% of calls, day or night, and book real jobs — not just messages",
    body: "AI voice agents pick up every call, ask the right qualifying questions for the trade, and book a structured job straight onto the dispatch board. When a call needs a human touch, the agent hands off to a live CSR with full context already captured — no cold transfer, no repeating the story.",
    bullets: [
      "Every call answered, including nights, weekends, and storm surges",
      "Structured intake becomes a bookable job in seconds, not a voicemail",
      "Clean handoff to a live CSR with full context when a call needs one",
    ],
    visual: {
      kind: "image",
      src: "/img/mktg-ai-voice.jpg",
      alt: "Visualization of a ServiTrex AI voice agent answering a customer call and booking a job in real time",
    },
  },
  {
    tag: "Accounting & inventory",
    title:
      "Job costing and parts tracking that finally match what happened in the field",
    body: "Labor, parts, and time roll up into job costing that reconciles against real work — not a monthly guessing game for the finance team. Truck stock, warehouse counts, and purchase orders stay visible, so a tech running low on a part is a dispatch decision, not a surprise on Friday.",
    bullets: [
      "Job costing tied to actual labor, parts, and time entries",
      "Truck stock, warehouse inventory, and POs in one view",
      "Financial close that reconciles against real job data, not estimates",
    ],
    visual: { kind: "icon", icon: icon.calc },
  },
  {
    tag: "Marketing automation",
    title:
      "Turn booked work into repeat work with campaigns built on real service history",
    body: "Trigger nurture sequences and renewal campaigns off actual service and membership data instead of exporting the customer list to a separate marketing tool. Review requests go out automatically after every closed job, and reporting connects the dollars spent to the jobs actually booked.",
    bullets: [
      "Campaigns triggered by service history, not a stale contact list",
      "Automated review requests after every closed-out job",
      "Reporting that connects marketing spend to booked revenue",
    ],
    visual: { kind: "icon", icon: icon.megaphone },
  },
];

const brandRail = [
  { name: "Harbor HVAC", note: "Parent brand · 4 zones" },
  { name: "CoastLine Plumbing", note: "DBA · shared dispatch" },
  { name: "Apex Electrical", note: "Regional brand · own pricebook" },
];

export function ProductOfficePage() {
  return (
    <>
      <Head
        title="ServiTrex Office — Dispatch, CRM, invoicing, and multi-brand ops"
        description="Go deep on the ServiTrex office product: dispatch, CRM, estimates and invoicing, memberships, AI voice call handling, accounting, inventory, and marketing automation — all on one multi-tenant platform."
      />

      <Section className="pt-14 sm:pt-16">
        <div className="grid items-center gap-10 lg:grid-cols-[1.05fr_0.95fr] lg:gap-12">
          <div>
            <Eyebrow>Product · Office</Eyebrow>
            <h1 className="max-w-3xl text-balance text-4xl sm:text-5xl lg:text-[3.3rem]">
              The office system that keeps CSRs, dispatch, and finance on the
              same page.
            </h1>
            <p className="mt-5 max-w-xl text-pretty text-lg text-muted sm:text-xl">
              ServiTrex Office is the command center behind every job: the board
              dispatchers work from, the CRM a returning customer's history
              lives in, the invoice that finally matches the field, and the AI
              voice layer that answers the phone when no one else can.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button to="/demo" size="lg">
                Book a demo
              </Button>
              <Button to="/pricing" variant="secondary" size="lg">
                View pricing
              </Button>
            </div>
          </div>

          <Reveal>
            <div className="relative">
              <div
                aria-hidden
                className="pointer-events-none absolute -inset-4 -z-10 rounded-[2.25rem] bg-teal/15 blur-2xl"
              />
              <div className="overflow-hidden rounded-[1.75rem] border border-border shadow-glow">
                <img
                  src="/img/mktg-hero-dashboard.jpg"
                  alt="ServiTrex office dashboard showing the live dispatch board, technician assignments, and today's schedule across brands"
                  width={960}
                  height={720}
                  className="aspect-[4/3] w-full object-cover"
                  loading="eager"
                  fetchPriority="high"
                />
              </div>
            </div>
          </Reveal>
        </div>
      </Section>

      <Section className="pt-2">
        <SectionHeading
          eyebrow="Inside the platform"
          title="Everything that touches a job, from first call to final invoice"
          description="Seven systems that used to live in seven different tools — dispatch, CRM, money, memberships, phones, back office, and growth — now share one job record."
        />

        <div className="mt-12 flex flex-col gap-16 sm:gap-20">
          {features.map((feature, index) => {
            const reversed = index % 2 === 1;
            return (
              <Reveal key={feature.title} delay={0.03}>
                <article
                  className={cn(
                    "grid items-center gap-8 lg:grid-cols-2 lg:gap-14",
                  )}
                >
                  <div className={cn(reversed && "lg:order-2")}>
                    {feature.visual.kind === "icon" ? (
                      <IconTile>{feature.visual.icon}</IconTile>
                    ) : (
                      <div className="overflow-hidden rounded-[1.5rem] border border-border shadow-soft">
                        <img
                          src={feature.visual.src}
                          alt={feature.visual.alt}
                          width={800}
                          height={600}
                          className="aspect-[4/3] w-full object-cover"
                          loading="lazy"
                        />
                      </div>
                    )}
                  </div>

                  <div className={cn(reversed && "lg:order-1")}>
                    {feature.visual.kind === "icon" && (
                      <p className="mb-4 text-xs font-semibold uppercase tracking-[0.16em] text-teal-deep">
                        {feature.tag}
                      </p>
                    )}
                    {feature.visual.kind === "image" && (
                      <p className="mb-4 text-xs font-semibold uppercase tracking-[0.16em] text-teal-deep">
                        {feature.tag}
                      </p>
                    )}
                    <h3 className="font-display text-2xl tracking-tight sm:text-3xl">
                      {feature.title}
                    </h3>
                    <p className="mt-4 text-pretty text-muted">
                      {feature.body}
                    </p>
                    <ul className="mt-5 space-y-2.5 text-sm text-fg">
                      {feature.bullets.map((point) => (
                        <li key={point} className="flex gap-2.5">
                          <span aria-hidden className="mt-0.5 text-teal-deep">
                            →
                          </span>
                          <span>{point}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </article>
              </Reveal>
            );
          })}
        </div>
      </Section>

      <Section className="bg-surface-muted/50">
        <div className="grid items-center gap-10 lg:grid-cols-[1fr_0.9fr] lg:gap-14">
          <div>
            <Eyebrow>Multi-tenant by design</Eyebrow>
            <h2 className="text-balance text-3xl text-fg sm:text-4xl lg:text-[2.6rem]">
              One platform, every brand you run.
            </h2>
            <p className="mt-4 max-w-xl text-pretty text-base text-muted sm:text-lg">
              Parent company, DBAs, and regional names each get their own
              pricebook, branding, and reporting — while dispatch, customer
              history, and reporting can roll up cleanly to the tenant when you
              want the full picture. No cloned databases, no exporting one
              brand's data to see across all of them.
            </p>
            <ul className="mt-6 space-y-2.5 text-sm text-fg">
              {[
                "Brand-scoped pricebooks, users, and access control",
                "Shared customers and properties where it helps, separated where it matters",
                "One bill, one login, unlimited brands on Enterprise",
              ].map((point) => (
                <li key={point} className="flex gap-2.5">
                  <span aria-hidden className="mt-0.5 text-teal-deep">
                    →
                  </span>
                  <span>{point}</span>
                </li>
              ))}
            </ul>
          </div>

          <Reveal>
            <div className="relative mx-auto w-full max-w-sm">
              <div className="absolute -left-6 top-10 hidden h-28 w-28 rounded-full bg-teal/20 blur-2xl sm:block" />
              <div className="relative overflow-hidden rounded-[1.75rem] border border-border bg-ink p-5 text-fg-inverse shadow-glow">
                <p className="text-xs font-semibold uppercase tracking-[0.16em] text-teal">
                  Tenant · Meridian Home Services Group
                </p>
                <div className="mt-4 space-y-3">
                  {brandRail.map((brand) => (
                    <div
                      key={brand.name}
                      className="rounded-2xl border border-white/10 bg-white/5 px-4 py-3"
                    >
                      <p className="font-display text-lg tracking-tight">
                        {brand.name}
                      </p>
                      <p className="mt-1 text-sm text-fg-inverse/70">
                        {brand.note}
                      </p>
                    </div>
                  ))}
                </div>
                <p className="mt-4 text-xs text-fg-inverse/60">
                  Switch brands without switching systems.
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </Section>

      <Section className="pt-6">
        <div className="flex flex-col items-start justify-between gap-4 rounded-[1.75rem] border border-dashed border-border bg-surface-raised p-6 sm:flex-row sm:items-center sm:p-8">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-teal-deep">
              The other half of the platform
            </p>
            <p className="mt-2 max-w-md text-pretty text-fg">
              Office is where jobs get created and closed out. TrexField is
              where they actually get done — see the technician side of the same
              system.
            </p>
          </div>
          <Button
            to="/product/field"
            variant="secondary"
            size="lg"
            className="shrink-0"
          >
            Explore TrexField
          </Button>
        </div>
      </Section>

      <CtaBand title="Tour the ServiTrex office board live" />
    </>
  );
}
